
import React from "react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface InfoBoxProps {
  title: string;
  content?: string;
  icon?: LucideIcon;
  className?: string;
  iconClassName?: string;
  children?: React.ReactNode;
  bulletPoints?: string[];
  subtitle?: string;
  highlightKeywords?: boolean;
}

const InfoBox = ({
  title,
  content,
  icon: Icon,
  className,
  iconClassName,
  children,
  bulletPoints,
  subtitle,
  highlightKeywords = false
}: InfoBoxProps) => {
  
  // Function to highlight keywords in text
  const highlightText = (text: string) => {
    if (!highlightKeywords) return text;
    
    // Words to highlight (nouns, numbers, specific terms)
    return text.split(' ').map((word, i) => {
      // Look for words that are numbers, percentages, or capitalized terms
      const shouldHighlight = 
        /^\d+%?$/.test(word) || // Numbers or percentages
        /^[A-Z][A-Za-z]*$/.test(word) || // Capitalized words
        /\([^)]*\)/.test(word) || // Text in parentheses
        /^[A-Z]{2,}$/.test(word) || // Acronyms (all caps)
        ['API', 'AI', 'DMS', 'CRM', 'V2', 'GTM', 'FTEs', 'CEO'].some(term => word.includes(term)); // Specific technical terms
        
      return shouldHighlight ? 
        <span key={i} className="font-bold text-blue-700 dark:text-blue-400">{word} </span> : 
        word + ' ';
    });
  };
  
  return (
    <div className={cn("bg-blue-50 p-6 rounded-lg shadow-sm", className)}>
      <div className="flex items-center mb-3">
        {Icon && (
          <div className={cn("w-10 h-10 flex items-center justify-center rounded-full bg-blue-100 mr-3", iconClassName)}>
            <Icon className="h-5 w-5 text-blue-600" />
          </div>
        )}
        <h3 className="font-space-grotesk font-bold text-lg">{title}</h3>
      </div>
      {subtitle && <h4 className="font-medium text-blue-700 mb-2">{subtitle}</h4>}
      {content && <p className="text-noa-paynes-gray text-sm mb-3">{highlightKeywords ? highlightText(content) : content}</p>}
      
      {bulletPoints && bulletPoints.length > 0 && (
        <ul className="space-y-2 mt-2">
          {bulletPoints.map((point, index) => (
            <li key={index} className="flex">
              <span className="text-blue-600 font-bold mr-2">•</span>
              <span className="text-noa-paynes-gray text-sm">{highlightKeywords ? highlightText(point) : point}</span>
            </li>
          ))}
        </ul>
      )}
      
      {children}
    </div>
  );
};

export { InfoBox };
